/**
 * Utility function to build alt text map from structure labels
 *
 * Per-feature naming: every atom-naming lookup goes through an
 * `AtomLabelResolver` built from the molecule's `SerializationResult`. The
 * resolver returns either the per-feature label (when given a context) or the
 * default-feature label (highest type priority). No global atom-label map.
 *
 * @package @alchemiesolns/svg-interactive
 */

import type { SVGStructure, FeatureNode, BondLeaf } from './types';
import type { WorkspaceFeatureResult } from '@alchemiesolns/feature-extractor-compat';
import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import {
  makeAtomLabelResolver,
  serializeFeatures,
  type AtomLabelResolver,
  type SerializationResult,
  type SerializedAtom,
  type SerializedFeature,
} from '@alchemiesolns/feature-extractor';
import {
  isPrimaryType,
  computePrimaryConnections,
  findBondBetweenAtoms,
  buildFeatureBlock,
  groupFunctionalGroupsByParent,
  classifyUnclaimedAtoms,
  groupUnclaimedAtomsByParent,
  type PrimaryConnection,
  type UnclaimedAtom,
} from './buildStructuredAltText';
import {
  classifyPrimaryConnection,
  classifyRelationship,
  formatConnectionPhrase,
  RECOGNIZED_BRIDGING_GROUPS,
} from '@alchemiesolns/molecule-descriptions';
import { describeSingleMolecule } from './workspaceSummaryTemplate';
import { findConnectedComponents } from './graphUtils';
import { describeAtom } from './atomDescription';
import { elementDisplayName, formatAtomNameWithCharge } from './atomNaming';

/**
 * PIPH-318 / PIPH-310: the resolver/`nameAtom` emits the SYMBOL charge form
 * ("Chlorine-4 +", "Aluminum -"). The HTML tree wants the verbal form
 * ("positively charged Chlorine-4"). Strip the trailing "+"/"-" marker (which
 * sits before " of " or at end) so `formatAtomNameWithCharge` can prepend the
 * prose prefix to a clean base. Mirrors the strip in the MC-905 announcer.
 */
const TRAILING_CHARGE_SYMBOL_RE = / *[+-]+( *)(?= of |$)/g;

function toVerbalCharge(name: string, charge: number): string {
  if (!charge) return name;
  const base = name.replace(TRAILING_CHARGE_SYMBOL_RE, (_, trailingSpace: string) => trailingSpace);
  return formatAtomNameWithCharge(base, charge);
}

/**
 * Label entry for a node in the HTML structure tree: short `name` used as the
 * accessible label, plus an optional longer `description` for screen readers.
 */
export interface TreeLabel {
  name: string;
  description?: string;
}

/**
 * Resolve `{name, description}` for a tree atom by looking up its serialized
 * counterpart and running the unified `describeAtom` over it. Returns
 * `{name}` only (no description) when the atom can't be matched — degenerate
 * inputs (missing feature result, hydrogen leaf with no serialized id) should
 * still produce a usable label.
 *
 * Joins by `SerializedAtom.id` (= workspace atom id) when present. The atom
 * descriptions only run for the workspace path, where ids are populated by
 * `feature-extractor-compat`; standalone graphs (test adapter) never reach
 * here, so the absent-id case isn't exercised in production.
 *
 * Per-atom labels are keyed by workspace atom id (`atom.atomId`) — one entry
 * per atom even when the atom appears under several feature subtrees. To keep
 * that single entry coherent, the name and description always come from the
 * atom's *default* feature (highest type priority per the resolver) rather
 * than the current loop's feature. This way a chain carbon that's also part
 * of an aldehyde reads "Carbon-3" from the chain everywhere it appears, not
 * bare "Carbon" inside the aldehyde subtree. PIPH-309 alignment: that
 * default-feature label is the IUPAC-reordered one from
 * `SerializedFeature.atomLabels`.
 */
function buildAtomEntry(
  atomId: string,
  atomLabel: string,
  serialized: SerializationResult,
  serializedAtomsById: Map<string, SerializedAtom>,
  resolve: AtomLabelResolver,
): TreeLabel {
  const serializedAtom = serializedAtomsById.get(atomId);
  if (!serializedAtom) return { name: atomLabel };
  const resolved = resolve(serializedAtom.index);
  // PIPH-310: render charge verbally ("positively charged Chlorine-2") rather
  // than the symbol form the resolver emits ("Chlorine-2 +").
  const name = toVerbalCharge(resolved || atomLabel, serializedAtom.charge);
  const description = describeAtom(serializedAtom, serialized, resolve);
  return { name, description };
}

/**
 * Phrase part of a bond label: "single" / "double" / "triple" / "wedge" / "dash"
 * / "squiggle". Mirrors `bondPhrase` in `nameBond.ts` but inlined here so we can
 * format with pre-resolved per-feature endpoint labels (the public helper takes
 * raw symbol + index/total pairs).
 */
function bondPhraseFromOrder(order: number, bondType?: 'l' | 'w' | 'd' | 's'): string {
  if (bondType === 'w') return 'wedge';
  if (bondType === 'd') return 'dash';
  if (bondType === 's') return 'squiggle';
  if (order === 3) return 'triple';
  if (order === 2) return 'double';
  return 'single';
}

/**
 * Extract a trailing `, <direction>` suffix that `createBondLeafForAtom` appends
 * for stereo-context bonds (PIPH-74). The "between X and Y" portion of a label
 * never contains a comma, so the first `, ` reliably marks the start of the
 * direction phrase. Returns the empty string when no suffix is present.
 */
function extractDirectionSuffix(label: string): string {
  const i = label.indexOf(', ');
  return i === -1 ? '' : label.substring(i);
}

/**
 * Rebuild a bond's display label using the default-feature labels from the
 * resolver. Keeps the "{phrase} bond between {a} and {b}[ , direction]" shape
 * produced by `nameBondBetween`. Each endpoint resolves in its own default
 * feature's scope rather than a forced parent-feature scope: the alt-text map
 * has one entry per bond, so the label must read the same way regardless of
 * which atom subtree the user reached the bond through. Falls back to the
 * tree's original label when either endpoint can't be resolved.
 */
function overlayBondLabel(
  bond: BondLeaf,
  serializedAtomsById: Map<string, SerializedAtom>,
  resolve: AtomLabelResolver,
): string {
  const [aId, bId] = bond.atomIds;
  const aSerialized = serializedAtomsById.get(aId);
  const bSerialized = serializedAtomsById.get(bId);
  if (!aSerialized || !bSerialized) return bond.label;
  const aLabel = resolve(aSerialized.index);
  const bLabel = resolve(bSerialized.index);
  if (!aLabel || !bLabel) return bond.label;
  // Preserve the parent → other endpoint ordering from createBondLeafForAtom
  // so the rebuilt label reads as "{parent} and {other}".
  const parentIsA = bond.parentAtomId === aId;
  const left = parentIsA ? aLabel : bLabel;
  const right = parentIsA ? bLabel : aLabel;
  const phrase = bondPhraseFromOrder(bond.order, bond.bondType);
  const direction = extractDirectionSuffix(bond.label);
  return `${phrase} bond between ${left} and ${right}${direction}`;
}

/**
 * Overlay bond labels and lone-pair labels under one feature subtree so the
 * tree displays the same default-feature labels `buildAtomEntry` already
 * applied to the atoms. Atom labels themselves are handled by
 * `buildAtomEntry`; this pass handles the bonds + lone pairs the caller wrote
 * to the map before calling here. Like atoms, bond / lone-pair labels resolve
 * via each atom's default feature (one entry per bond/lone-pair regardless of
 * which subtree exposes them) — see `overlayBondLabel` for the rationale.
 *
 * Lone-pair labels embed the parent atom's display label: the lone-pair
 * factory in `atomUtils.ts` builds names like "lone pair 1 on Carbon-3", so
 * we rewrite the parse-order parent name with the resolved one. No-op for
 * atoms / bonds that don't resolve — the tree's original label stands.
 */
function overlayFeatureChildLabels(
  feature: FeatureNode,
  map: TreeLabelMap,
  serializedAtomsById: Map<string, SerializedAtom>,
  resolve: AtomLabelResolver,
): void {
  for (const atom of feature.atoms) {
    const serializedAtom = serializedAtomsById.get(atom.atomId);
    if (serializedAtom) {
      const resolved = resolve(serializedAtom.index);
      if (resolved && resolved !== atom.label) {
        for (const lp of atom.lonePairNodes) {
          const lpEntry = map.get(lp.lonePairId);
          if (lpEntry) {
            map.set(lp.lonePairId, { ...lpEntry, name: lpEntry.name.replace(atom.label, resolved) });
          }
        }
      }
    }

    for (const bond of atom.bondNodes) {
      const newLabel = overlayBondLabel(bond, serializedAtomsById, resolve);
      if (newLabel !== bond.label) {
        const existing = map.get(bond.bondId);
        if (existing) {
          map.set(bond.bondId, { ...existing, name: newLabel });
        }
      }
    }
  }
}

/**
 * Map of element ID → `TreeLabel`. Produced by `buildAltTextMapFromStructure`
 * and consumed by `HTMLStructureTree` / `NavigableTree`.
 */
export type TreeLabelMap = Map<string, TreeLabel>;

// ============================================================================
// PIPH-139: Bridging Group Helpers
// ============================================================================

/**
 * PIPH-139 / PIPH-212: RECOGNIZED_BRIDGING_GROUPS now lives in
 * `@alchemiesolns/molecule-descriptions` and is imported above.
 */
function isRecognizedBridgingGroup(feature: SerializedFeature): boolean {
  return RECOGNIZED_BRIDGING_GROUPS.has(feature.internalName);
}

/**
 * PIPH-139: Format bond order label including single bonds.
 */
function formatBondOrderLabelFull(order: number | undefined): string {
  if (order === 3) return 'triple bond';
  if (order === 2) return 'double bond';
  return 'single bond';
}

/**
 * PIPH-132 / PIPH-316: Build a description for a functional group, used as
 * the screen-reader `description` on the SVG navigation tree's FG node.
 *
 * Format (PIPH-316): `"{relationship} {location}. {bonding pattern}"`. The FG
 * name itself is established by the tree hierarchy and isn't repeated.
 *
 * The relationship word and the parents come from `classifyRelationship`
 * (PIPH-60); the bonding-pattern sentence (when implemented for the FG type)
 * comes from the per-internalName dispatch in `buildFGBondingPattern`. FG
 * types whose template hasn't been pinned down — anhydride, nitro,
 * lactone/lactam in-ring, own-carbon nitrile — fall through to location-only.
 */
export function buildFunctionalGroupDescription(
  fg: SerializedFeature,
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): string {
  // PIPH-316: relationship classifier drives both the location-sentence shape
  // ("At … of …" / "In …" / "Between … and …") and which parent feature(s)
  // the bonding pattern references.
  const relationship = classifyRelationship(fg, serialized);
  const parents = relationship.parentIds
    .map(id => serialized.features.find(f => f.id === id))
    .filter((f): f is SerializedFeature => !!f)
    // Sort by feature ID so output is deterministic across runs — the classifier
    // returns parentIds in set-iteration order which is not reproducible.
    .sort((a, b) => a.id.localeCompare(b.id));

  // PIPH-316: lactone/lactam classify as "at" (carbonyl C shared with the ring)
  // but read as in-ring features ("In {ring}. … at position Y."). Force "in" so
  // both the location sentence and the bonding pattern use the in-ring form.
  const kind = isInRingLactoneLactam(fg, parents, serialized) ? 'in' : relationship.kind;

  const location = buildFGLocationSentence(fg, kind, parents, resolve);
  if (!location) return '';

  const bonding = buildFGBondingPattern(fg, kind, parents, serialized, resolve);
  return bonding ? `${location} ${bonding}` : location;
}

/**
 * PIPH-316: Build the location sentence per relationship kind.
 *   at      → "At carbon-X of {parent}."
 *   in      → "In {parent}."
 *   between → "Between {parent1} and {parent2}."
 */
function buildFGLocationSentence(
  fg: SerializedFeature,
  kind: 'at' | 'in' | 'between',
  parents: SerializedFeature[],
  resolve: AtomLabelResolver,
): string {
  if (kind === 'at') {
    const parent = parents[0];
    if (!parent) return '';
    const connectionAtomIndex = fg.connectionAtomIndices[0];
    if (connectionAtomIndex === undefined) return '';
    const positionLabel = getPositionLabel(connectionAtomIndex, parent.id, resolve);
    return `At ${positionLabel} of ${parent.name}.`;
  }
  if (kind === 'in') {
    const parent = parents[0];
    if (!parent) return '';
    return `In ${parent.name}.`;
  }
  // between — list ALL parents (PIPH-316 bug 3: tertiary amine bridges 3+).
  if (parents.length < 2) return '';
  return `Between ${oxfordJoin(parents.map(p => p.name))}.`;
}

/**
 * Join names with commas and a trailing "and" (Oxford comma for 3+):
 *   ["A","B"] → "A and B"; ["A","B","C"] → "A, B, and C".
 */
function oxfordJoin(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * PIPH-316: Look up an atom's per-feature position label (e.g. "carbon-3"),
 * normalized to lowercase and with any trailing charge/radical marker stripped
 * (the marker would have been added by `nameAtom` for atoms in a non-default
 * feature; the location sentence never wants it).
 */
function getPositionLabel(
  atomIdx: number,
  featureId: string,
  resolve: AtomLabelResolver,
): string {
  const raw = resolve(atomIdx, featureId);
  if (!raw) return 'atom-?';
  return raw.replace(/\s*\d*[+-]\s*$/, '').toLowerCase();
}

/**
 * PIPH-316: Just the trailing digit from a per-feature position label, used by
 * in-ring heteroatom bonding-pattern templates that read "NH at position 1" /
 * "O at position 2". The element prefix is already named earlier in the
 * sentence; only the position number is needed here.
 */
function getRingPositionNumber(
  atomIdx: number,
  featureId: string,
  resolve: AtomLabelResolver,
): string {
  const raw = resolve(atomIdx, featureId) ?? '';
  const match = raw.match(/-(\d+)\b/);
  return match ? match[1] : '?';
}

/**
 * PIPH-316: Bond word between two atoms — "wedge bonded" / "dash bonded" /
 * "single bonded" for the own-atom FG templates. Returns "single bonded" as
 * the fallback when no bond is found (defensive — should never trigger for
 * recognized FGs whose connection atoms are bonded by construction).
 */
function getBondWord(
  atomIdx1: number,
  atomIdx2: number,
  serialized: SerializationResult,
): string {
  const bond = findBondBetweenAtoms(atomIdx1, atomIdx2, serialized);
  if (!bond) return 'single bonded';
  if (bond.bondType === 'w') return 'wedge bonded';
  if (bond.bondType === 'd') return 'dash bonded';
  if (bond.bondType === 's') return 'squiggle bonded';
  return 'single bonded';
}

/** PIPH-316: Capitalize "carbon-3" → "Carbon-3" for the start of a sentence. */
function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * PIPH-316: Find the FG's "own" heteroatom (first non-H heavy atom in
 * `fg.atomIndices`). Used both by own-atom "at" templates (alcohol, halide,
 * thiol, amine.1, methyl, isopropyl — whose atomIndices contains only the
 * FG's own atoms, NOT the parent's carbon) and by "in" / "between" templates
 * (ether, sulfide, amine.2/.3 — whose atomIndices contains just the
 * heteroatom plus any bonded H). Returns undefined for empty/all-H FGs.
 */
function findOwnHeteroatomIdx(
  fg: SerializedFeature,
  serialized: SerializationResult,
): number | undefined {
  for (const idx of fg.atomIndices) {
    const atom = serialized.atoms.find(a => a.index === idx);
    if (!atom || atom.symbol === 'H') continue;
    return idx;
  }
  return undefined;
}

/**
 * PIPH-316: Find which of the FG's connection atoms belongs to a given parent
 * feature. Used by `between`-relationship templates to pair each connection
 * atom with the right parent.
 */
function findConnectionInParent(
  fg: SerializedFeature,
  parent: SerializedFeature,
): number | undefined {
  const parentSet = new Set(parent.atomIndices);
  for (const idx of fg.connectionAtomIndices) {
    if (parentSet.has(idx)) return idx;
  }
  return undefined;
}

/**
 * PIPH-316: Find the FG's non-carbonyl heteroatom (symbol `sym`) that is also a
 * member of the given ring. For a lactone this is the ester alkoxy O in the
 * ring; for a lactam, the amide N in the ring.
 */
function findRingHeteroatom(
  fg: SerializedFeature,
  ring: SerializedFeature,
  sym: string,
  serialized: SerializationResult,
): number | undefined {
  return fg.atomIndices.find(ai => {
    if (!ring.atomIndices.includes(ai)) return false;
    return serialized.atoms.find(a => a.index === ai)?.symbol === sym;
  });
}

/**
 * PIPH-316 (round 3): collect every atom index that belongs to a ring or
 * bridged-bicyclic primary feature. Mirrors `collectRingAtomIndices` in
 * `buildStructuredAltText.ts` (not exported) — used to suppress E/Z labels on
 * ring-internal alkenes, whose double-bond geometry is fixed by the ring (no
 * rotation, so no E/Z isomerism to describe).
 */
function collectRingAtomIndices(serialized: SerializationResult): Set<number> {
  const inRing = new Set<number>();
  for (const f of serialized.features) {
    if (f.type === 'ring' || f.type === 'bridged_bicyclic') {
      for (const idx of f.atomIndices) inRing.add(idx);
    }
  }
  return inRing;
}

/**
 * PIPH-316: True when `fg` is a cyclic ester/amide (lactone/lactam) — a
 * carbonyl.ester / carbonyl.amide.2 / carbonyl.amide.3 whose non-carbonyl
 * heteroatom (O for ester, N for amide) is a member of the single parent RING.
 * Such groups classify as "at" but should read as in-ring ("In {ring}. …").
 */
function isInRingLactoneLactam(
  fg: SerializedFeature,
  parents: SerializedFeature[],
  serialized: SerializationResult,
): boolean {
  const name = fg.internalName;
  if (name !== 'carbonyl.ester' && name !== 'carbonyl.amide.2' && name !== 'carbonyl.amide.3') {
    return false;
  }
  if (parents.length !== 1 || parents[0].type !== 'ring') return false;
  const sym = name === 'carbonyl.ester' ? 'O' : 'N';
  return findRingHeteroatom(fg, parents[0], sym, serialized) !== undefined;
}

/**
 * PIPH-316 (round 3): Locate the three key atoms of an own-carbon carbonyl FG
 * (ester / amide whose carbonyl carbon is NOT shared with a parent). Returns:
 *   - `carbonIdx`   the carbonyl carbon (the C in `fg.atomIndices` order-2
 *                   bonded to an O also in `fg.atomIndices`),
 *   - `heteroIdx`   the ester alkoxy O / amide N — the other heteroatom in
 *                   `fg.atomIndices` that is single-bonded to the carbonyl C.
 * Returns undefined when the FG doesn't match this shape (defensive).
 */
function findOwnCarbonylAtoms(
  fg: SerializedFeature,
  serialized: SerializationResult,
): { carbonIdx: number; heteroIdx: number } | undefined {
  const fgSet = new Set(fg.atomIndices);
  const symOf = (idx: number) => serialized.atoms.find(a => a.index === idx)?.symbol;
  // Carbonyl carbon: a C in the FG with an order-2 bond to an O in the FG.
  let carbonIdx: number | undefined;
  for (const idx of fg.atomIndices) {
    if (symOf(idx) !== 'C') continue;
    const dblToO = serialized.bonds.some(b => {
      if (b.order !== 2) return false;
      const [x, y] = b.atomIndices;
      const other = x === idx ? y : y === idx ? x : undefined;
      return other !== undefined && fgSet.has(other) && symOf(other) === 'O';
    });
    if (dblToO) { carbonIdx = idx; break; }
  }
  if (carbonIdx === undefined) return undefined;
  // Heteroatom: an O (ester) or N (amide) in the FG single-bonded to that C.
  const isAmide = fg.internalName?.startsWith('carbonyl.amide');
  const heteroSym = isAmide ? 'N' : 'O';
  let heteroIdx: number | undefined;
  for (const b of serialized.bonds) {
    if (b.order !== 1) continue;
    const [x, y] = b.atomIndices;
    const other = x === carbonIdx ? y : y === carbonIdx ? x : undefined;
    if (other === undefined || !fgSet.has(other)) continue;
    if (symOf(other) === heteroSym) { heteroIdx = other; break; }
  }
  if (heteroIdx === undefined) return undefined;
  return { carbonIdx, heteroIdx };
}

/**
 * PIPH-316 (round 3): For an own-carbon carbonyl bridge, find which parent the
 * atom `fgAtomIdx` single-bonds into, and the label of the parent atom it bonds
 * to (e.g. "carbon-1"). Used to order parents SEMANTICALLY: the carbonyl carbon
 * names the first parent; the heteroatom names the rest. Returns one entry per
 * (parent, parentAtom) bond — the heteroatom of a tertiary amide bonds two.
 */
function findParentBondsFor(
  fgAtomIdx: number,
  parents: SerializedFeature[],
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): { parent: SerializedFeature; label: string }[] {
  const out: { parent: SerializedFeature; label: string }[] = [];
  for (const b of serialized.bonds) {
    const [x, y] = b.atomIndices;
    const other = x === fgAtomIdx ? y : y === fgAtomIdx ? x : undefined;
    if (other === undefined) continue;
    const parent = parents.find(p => p.atomIndices.includes(other));
    if (parent) out.push({ parent, label: getPositionLabel(other, parent.id, resolve) });
  }
  return out;
}

/**
 * PIPH-316: The per-FG-type bonding-pattern sentence appended after the
 * location sentence. Returns `''` for FG types whose template hasn't been
 * pinned down (anhydride, nitro, lactone/lactam in-ring, own-carbon nitrile)
 * — the description falls through to location-only, preserving the pre-card
 * behavior.
 */
function buildFGBondingPattern(
  fg: SerializedFeature,
  kind: 'at' | 'in' | 'between',
  parents: SerializedFeature[],
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): string {
  const name = fg.internalName;
  if (!name) return '';

  // Halides family — own-atom; bond between halide atom and parent C.
  // `"{Sym} {bond} to carbon-X."`
  const halideMap: Record<string, string> = {
    'alkylHalide.flouride': 'F',
    'alkylHalide.chloride': 'Cl',
    'alkylHalide.bromide': 'Br',
    'alkylHalide.iodide': 'I',
  };
  if (name in halideMap && kind === 'at' && parents.length === 1) {
    return buildOwnAtomAtTemplate(fg, parents[0], halideMap[name], serialized, resolve);
  }

  // Other own-atom "at" FGs sharing the same template shape.
  const ownAtomAtMap: Record<string, string> = {
    'alcohol': 'OH',
    'thiol': 'SH',
    'amine.1': 'NH₂',
    'chain.1.methyl': 'CH₃',
  };
  if (name in ownAtomAtMap && kind === 'at' && parents.length === 1) {
    return buildOwnAtomAtTemplate(fg, parents[0], ownAtomAtMap[name], serialized, resolve);
  }

  // Isopropyl — own-atom "at" with an extra trailing clause for the two CH₃.
  if (name === 'isopropyl' && kind === 'at' && parents.length === 1) {
    const base = buildOwnAtomAtTemplate(fg, parents[0], 'CH', serialized, resolve);
    if (!base) return '';
    // Insert ", single bonded to two CH₃" before the trailing period.
    return base.replace(/\.\s*$/, ', single bonded to two CH₃.');
  }

  // PIPH-316: Lactone / lactam — a cyclic ester/amide whose non-carbonyl
  // heteroatom (ester alkoxy O / amide N) is itself a RING member. `kind` was
  // forced to "in" by the caller (isInRingLactoneLactam). Emit the in-ring
  // phrasing keyed on the heteroatom's ring position.
  if (
    (name === 'carbonyl.ester' || name === 'carbonyl.amide.2' || name === 'carbonyl.amide.3') &&
    kind === 'in' &&
    parents.length === 1
  ) {
    const ring = parents[0];
    const carbonIdx = fg.connectionAtomIndices[0];
    const ringHeteroIdx = findRingHeteroatom(fg, ring, name === 'carbonyl.ester' ? 'O' : 'N', serialized);
    if (carbonIdx !== undefined && ringHeteroIdx !== undefined) {
      const carbonNum = capitalizeFirst(getPositionLabel(carbonIdx, ring.id, resolve)).replace(/^Carbon-/, '');
      const pos = getRingPositionNumber(ringHeteroIdx, ring.id, resolve);
      const tail = name === 'carbonyl.ester' ? 'O' : (name === 'carbonyl.amide.2' ? 'NH' : 'N');
      return `Carbon-${carbonNum} double bonded to O, single bonded to ${tail} at position ${pos}.`;
    }
    return '';
  }

  // PIPH-316 (round 3): Own-carbon ester / amide bridges — the carbonyl carbon
  // is NOT shared with any parent (it's the FG's own atom, bonded TO a parent),
  // so the FG classifies "between". The location sentence already reads
  // "Between {parents}."; append the bonding pattern with the carbonyl carbon
  // named bare "C" (own-carbon convention, matching own-carbon nitrile above).
  // Parents are ordered SEMANTICALLY here, not by the caller's id-sort: the
  // carbonyl carbon names the first parent (carbon-X), the heteroatom names the
  // rest (carbon-Y/Z).
  if (
    (name === 'carbonyl.ester' || name === 'carbonyl.amide.2' || name === 'carbonyl.amide.3') &&
    kind === 'between' &&
    parents.length >= 2
  ) {
    const atoms = findOwnCarbonylAtoms(fg, serialized);
    if (!atoms) return '';
    const cBonds = findParentBondsFor(atoms.carbonIdx, parents, serialized, resolve);
    const hBonds = findParentBondsFor(atoms.heteroIdx, parents, serialized, resolve);
    if (cBonds.length === 0 || hBonds.length === 0) return '';
    const carbonX = cBonds[0];

    if (name === 'carbonyl.ester') {
      // "C double bonded to O, single bonded to carbon-X of {p1} and O bonded to carbon-Y of {p2}."
      const carbonY = hBonds[0];
      return `C double bonded to O, single bonded to ${carbonX.label} of ${carbonX.parent.name} ` +
        `and O bonded to ${carbonY.label} of ${carbonY.parent.name}.`;
    }
    if (name === 'carbonyl.amide.2') {
      // "C double bonded to O, single bonded to carbon-X of {p1} and NH bonded to carbon-Y of {p2}."
      const carbonY = hBonds[0];
      return `C double bonded to O, single bonded to ${carbonX.label} of ${carbonX.parent.name} ` +
        `and NH bonded to ${carbonY.label} of ${carbonY.parent.name}.`;
    }
    // carbonyl.amide.3 — the N bonds two parents.
    // "C double bonded to O, single bonded to carbon-X of {p1}. N bonded to carbon-Y of {p2} and carbon-Z of {p3}."
    if (hBonds.length < 2) return '';
    const nClauses = hBonds.map(h => `${h.label} of ${h.parent.name}`);
    return `C double bonded to O, single bonded to ${carbonX.label} of ${carbonX.parent.name}. ` +
      `N bonded to ${oxfordJoin(nClauses)}.`;
  }

  // Shared-carbon carbonyls — no heteroatom variable; only the position varies.
  const sharedCarbonylMap: Record<string, string> = {
    'carbonyl.aldehyde': 'Carbon-{X} double bonded to O.',
    'carbonyl.ketone': 'Carbon-{X} double bonded to O.',
    'carbonyl': 'Carbon-{X} double bonded to O.',
    'carbonyl.carboxylicAcid': 'Carbon-{X} double bonded to O, single bonded to OH.',
    'carbonyl.amide.1': 'Carbon-{X} double bonded to O, single bonded to NH₂.',
    'carbonyl.amide.2': 'Carbon-{X} double bonded to O, single bonded to NH.',
    'carbonyl.amide.3': 'Carbon-{X} double bonded to O, single bonded to N.',
    'carbonyl.ester': 'Carbon-{X} double bonded to O, single bonded to O.',
    'carbonyl.acidHalide.flouride': 'Carbon-{X} double bonded to O, single bonded to F.',
    'carbonyl.acidHalide.chloride': 'Carbon-{X} double bonded to O, single bonded to Cl.',
    'carbonyl.acidHalide.bromide': 'Carbon-{X} double bonded to O, single bonded to Br.',
    'carbonyl.acidHalide.iodide': 'Carbon-{X} double bonded to O, single bonded to I.',
  };
  if (name in sharedCarbonylMap && kind === 'at' && parents.length === 1) {
    // The {X} position is the carbonyl carbon — same atom as the FG's
    // connection atom (carbonyl C is shared with the parent's atom list).
    const carbonIdx = fg.connectionAtomIndices[0];
    if (carbonIdx === undefined) return '';
    const carbonLabel = capitalizeFirst(getPositionLabel(carbonIdx, parents[0].id, resolve));
    return sharedCarbonylMap[name].replace('{X}', carbonLabel.replace(/^Carbon-/, ''));
  }

  // Nitrile — shared-carbon variant only (own-carbon deferred per the card).
  if (name === 'nitrile' && kind === 'at' && parents.length === 1) {
    const carbonIdx = fg.connectionAtomIndices[0];
    if (carbonIdx === undefined) return '';
    // Shared-carbon check: the connection atom is also in fg.atomIndices.
    if (!fg.atomIndices.includes(carbonIdx)) {
      // PIPH-316: OWN-carbon nitrile — the nitrile C is the FG's own atom
      // (not in the parent), single-bonded to the parent carbon (connection).
      // "C triple bonded to N, single bonded to carbon-X."
      const carbonLabel = getPositionLabel(carbonIdx, parents[0].id, resolve);
      return `C triple bonded to N, single bonded to ${carbonLabel}.`;
    }
    const carbonNum = capitalizeFirst(getPositionLabel(carbonIdx, parents[0].id, resolve))
      .replace(/^Carbon-/, '');
    return `Carbon-${carbonNum} triple bonded to N.`;
  }

  // Ether / sulfide — bridging "between" primaries (own-atom).
  if ((name === 'ether' || name === 'sulfide') && kind === 'between' && parents.length >= 2) {
    return buildBridgingHeteroatomTemplate(fg, parents, name === 'ether' ? 'O' : 'S', resolve);
  }
  // Ether / sulfide in-ring: "{Sym} at position X".
  if ((name === 'ether' || name === 'sulfide') && kind === 'in' && parents.length === 1) {
    const heteroIdx = findOwnHeteroatomIdx(fg, serialized);
    if (heteroIdx === undefined) return '';
    const pos = getRingPositionNumber(heteroIdx, parents[0].id, resolve);
    return `${name === 'ether' ? 'O' : 'S'} at position ${pos}.`;
  }

  // Secondary / tertiary amine — bridging (2+ parents) or in-ring.
  if ((name === 'amine.2' || name === 'amine.3') && kind === 'between' && parents.length >= 2) {
    return buildBridgingHeteroatomTemplate(fg, parents, name === 'amine.2' ? 'NH' : 'N', resolve);
  }
  if ((name === 'amine.2' || name === 'amine.3') && kind === 'in' && parents.length === 1) {
    const heteroIdx = findOwnHeteroatomIdx(fg, serialized);
    if (heteroIdx === undefined) return '';
    const pos = getRingPositionNumber(heteroIdx, parents[0].id, resolve);
    return `${name === 'amine.2' ? 'NH' : 'N'} at position ${pos}.`;
  }

  // Alkene / alkyne — in (single feature) or between (two features).
  if (name === 'alkene' || name === 'alkyne') {
    const orderWord = name === 'alkene' ? 'Double' : 'Triple';
    if (kind === 'in' && parents.length === 1) {
      // Both atoms of the pi bond are in the parent feature. They're the FG's
      // atomIndices (size 2 for alkene/alkyne). Order by per-feature position.
      const labels = fg.atomIndices
        .map(idx => ({ idx, label: getPositionLabel(idx, parents[0].id, resolve) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      if (labels.length !== 2) return '';
      let sentence = `${orderWord} bond between ${labels[0].label} and ${labels[1].label}.`;
      // PIPH-316 (round 3): surface E/Z on the alkene pi bond, mirroring the
      // PIPH-269 feature-block treatment. Skip ring-internal double bonds — the
      // ring fixes the geometry, so there's no E/Z isomerism (and PIPH-269's
      // skip rule does the same). `isomerConfig` lives on the order-2 bond
      // between the FG's two heavy atoms.
      if (name === 'alkene') {
        const piBond = findBondBetweenAtoms(labels[0].idx, labels[1].idx, serialized);
        const ringAtoms = collectRingAtomIndices(serialized);
        const inRing = ringAtoms.has(labels[0].idx) && ringAtoms.has(labels[1].idx);
        if (piBond?.isomerConfig && !inRing) {
          const tag = piBond.isomerConfig === 'e' ? 'E' : 'Z';
          sentence += ` ${tag} configuration.`;
        }
      }
      return sentence;
    }
    if (kind === 'between' && parents.length === 2) {
      const [p1, p2] = parents;
      const c1 = findConnectionInParent(fg, p1);
      const c2 = findConnectionInParent(fg, p2);
      if (c1 === undefined || c2 === undefined) return '';
      let sentence = `${orderWord} bond between ${getPositionLabel(c1, p1.id, resolve)} of ${p1.name} and ${getPositionLabel(c2, p2.id, resolve)} of ${p2.name}.`;
      // PIPH-316 (round 3b): surface E/Z on a BETWEEN-feature alkene too. The
      // two connection carbons ARE the C=C endpoints, and the pi bond (with its
      // isomerConfig) is in serialized.bonds regardless of feature membership.
      // No ring-skip needed — a between-feature alkene is never ring-internal
      // (both carbons in one ring would classify "in", not "between"). This
      // solves PIPH-269's between-feature limitation on the SVG-tree surface.
      if (name === 'alkene') {
        const piBond = findBondBetweenAtoms(c1, c2, serialized);
        if (piBond?.isomerConfig) {
          sentence += ` ${piBond.isomerConfig === 'e' ? 'E' : 'Z'} configuration.`;
        }
      }
      return sentence;
    }
  }

  // PIPH-316: Anhydride (between two primaries). Each side's carbonyl carbon is
  // a connection atom in one parent; both single-bond the shared bridging O.
  // "Carbon-X of {p1} double bonded to O, carbon-Y of {p2} double bonded to O,
  //  both single bonded to bridging O."
  if (name === 'carbonyl.acidAnhydride' && kind === 'between' && parents.length === 2) {
    const [p1, p2] = parents;
    const c1 = findConnectionInParent(fg, p1);
    const c2 = findConnectionInParent(fg, p2);
    if (c1 === undefined || c2 === undefined) return '';
    const l1 = capitalizeFirst(getPositionLabel(c1, p1.id, resolve));
    const l2 = getPositionLabel(c2, p2.id, resolve);
    return `${l1} of ${p1.name} double bonded to O, ${l2} of ${p2.name} double bonded to O, both single bonded to bridging O.`;
  }

  // PIPH-316: Nitro (at). N⁺ single bonded to the parent carbon, double bonded
  // to one O, single bonded to a negatively charged O. The N is the FG's own
  // heavy atom; the parent carbon is the connection atom.
  if (name === 'nitro' && kind === 'at' && parents.length === 1) {
    const carbonIdx = fg.connectionAtomIndices[0];
    if (carbonIdx === undefined) return '';
    const carbonLabel = getPositionLabel(carbonIdx, parents[0].id, resolve);
    return `Positively charged N single bonded to ${carbonLabel}, double bonded to O, single bonded to negatively charged O.`;
  }

  // Deferred / unrecognized FG types — location-only behavior preserved.
  return '';
}

/**
 * PIPH-316: Build the "{symbol} {bond} to carbon-X." template shared by all
 * own-atom "at" FGs (alcohol, halide, amine, thiol, methyl, isopropyl base).
 */
function buildOwnAtomAtTemplate(
  fg: SerializedFeature,
  parent: SerializedFeature,
  symbol: string,
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): string {
  const heteroIdx = findOwnHeteroatomIdx(fg, serialized);
  const carbonIdx = fg.connectionAtomIndices[0];
  if (heteroIdx === undefined || carbonIdx === undefined) return '';
  const bondWord = getBondWord(heteroIdx, carbonIdx, serialized);
  const carbonLabel = getPositionLabel(carbonIdx, parent.id, resolve);
  return `${symbol} ${bondWord} to ${carbonLabel}.`;
}

/**
 * PIPH-316: Build the "{symbol} single bonded to carbon-X of {p1}, carbon-Y of
 * {p2}, and carbon-Z of {p3}." template shared by bridging ether/sulfide/amine
 * FGs. Handles 2+ parents (PIPH-316 bug 3: tertiary amine can bridge 3).
 */
function buildBridgingHeteroatomTemplate(
  fg: SerializedFeature,
  parents: SerializedFeature[],
  symbol: string,
  resolve: AtomLabelResolver,
): string {
  const clauses: string[] = [];
  for (const p of parents) {
    const c = findConnectionInParent(fg, p);
    if (c === undefined) return '';
    clauses.push(`${getPositionLabel(c, p.id, resolve)} of ${p.name}`);
  }
  if (clauses.length < 2) return '';
  return `${symbol} single bonded to ${oxfordJoin(clauses)}.`;
}

/**
 * PIPH-112: Build a connection description string for a primary feature.
 * Returns a string like "Connected at carbon-3 to 4-carbon chain." or empty
 * string if no connections.
 *
 * The source atom of every connection is in `feature` (the source primary),
 * so we resolve all source-atom labels using `feature.id` as the resolver
 * context. This makes the per-feature label authoritative for the source
 * position name.
 */
export function buildFeatureConnectionDescription(
  featureId: string,
  serialized: SerializationResult,
  primaries: SerializedFeature[],
  connectionMap: Map<string, PrimaryConnection[]>,
  resolve: AtomLabelResolver,
): string {
  const connections = connectionMap.get(featureId);
  if (!connections || connections.length === 0) return '';

  const feature = primaries.find(f => f.id === featureId);
  if (!feature) return '';

  const connectionLines: string[] = [];

  for (const conn of connections) {
    const targetFeature = primaries.find(p => p.id === conn.targetFeatureId);
    if (!targetFeature) continue;

    // Find which atom in this feature forms the connection.
    // The source atom is in `feature`, so we resolve in `feature.id`'s scope.
    let sourceAtomLabel: string | undefined;
    let connectionBond: { order: number | undefined } | undefined;

    // PIPH-212: A "bridging group" connection may actually be a direct bond
    // (alkene/alkyne between two primaries). Reclassify and route accordingly.
    if (conn.bridgingGroupId) {
      const bridgingGroup = serialized.features.find(f => f.id === conn.bridgingGroupId);
      if (bridgingGroup) {
        const info = classifyPrimaryConnection(bridgingGroup, feature, targetFeature, serialized);

        if (info.kind !== 'direct') {
          // Find bond from feature to bridging group (for unrecognized fallback bond order).
          for (let i = 0; i < feature.atomIndices.length; i++) {
            const featureAtomIdx = feature.atomIndices[i];
            for (const bgAtomIdx of bridgingGroup.atomIndices) {
              const bond = findBondBetweenAtoms(featureAtomIdx, bgAtomIdx, serialized);
              if (bond) {
                sourceAtomLabel = resolve(featureAtomIdx, feature.id);
                connectionBond = bond;
                break;
              }
            }
            if (sourceAtomLabel) break;
          }

          let bridgingPhrase: string;
          if (isRecognizedBridgingGroup(bridgingGroup)) {
            bridgingPhrase = `through ${bridgingGroup.name}`;
          } else {
            const bondLabel = formatBondOrderLabelFull(connectionBond?.order);
            // PIPH-310: When the unrecognized bridging group is a singleton
            // atom feature (e.g. a lone charged or radical heteroatom acting
            // as a bridge), substitute the prose form with charge/radical
            // adjectives. Multi-atom bridging groups keep their formula-style
            // name (out of card scope).
            const targetName =
              bridgingGroup.type === 'atom' &&
              bridgingGroup.atomIndices.length === 1
                ? (() => {
                    const a = serialized.atoms.find(
                      x => x.index === bridgingGroup.atomIndices[0],
                    );
                    return a
                      ? elementDisplayName(a.symbol, {
                          charge: a.charge,
                          radicals: a.radicals,
                        })
                      : bridgingGroup.name;
                  })()
                : bridgingGroup.name;
            bridgingPhrase = `through ${bondLabel} to ${targetName}`;
          }

          if (sourceAtomLabel) {
            connectionLines.push(`Connected at ${sourceAtomLabel.toLowerCase()} ${bridgingPhrase} to ${targetFeature.name}.`);
          } else {
            connectionLines.push(`Connected ${bridgingPhrase} to ${targetFeature.name}.`);
          }
          continue;
        }
        // info.kind === 'direct' — fall through and emit a direct-connection
        // line below using the bond order ("by double bond").
      }
    }

    // Direct connection (no bridging group, OR a "between" group with no extra
    // heavy atoms — alkene/alkyne between two primaries).
    let directBondOrder: number | undefined;
    for (let i = 0; i < feature.atomIndices.length; i++) {
      const featureAtomIdx = feature.atomIndices[i];
      for (const targetAtomIdx of targetFeature.atomIndices) {
        const bond = findBondBetweenAtoms(featureAtomIdx, targetAtomIdx, serialized);
        if (bond) {
          sourceAtomLabel = resolve(featureAtomIdx, feature.id);
          directBondOrder = bond.order;
          break;
        }
      }
      if (sourceAtomLabel) break;
    }

    // PIPH-212: Include "by double bond" / "by triple bond" qualifier when the
    // connecting bond is multi-order. Single bonds keep the bare wording.
    const phrase = formatConnectionPhrase({
      kind: 'direct',
      group: feature,
      bondOrder: (directBondOrder === 2 || directBondOrder === 3 ? directBondOrder : 1),
    });
    const includePhrase = directBondOrder === 2 || directBondOrder === 3;

    if (sourceAtomLabel) {
      const middle = includePhrase ? ` ${phrase}` : '';
      connectionLines.push(`Connected at ${sourceAtomLabel.toLowerCase()}${middle} to ${targetFeature.name}.`);
    } else if (includePhrase) {
      connectionLines.push(`Connected ${phrase} to ${targetFeature.name}.`);
    } else {
      connectionLines.push(`Connected to ${targetFeature.name}.`);
    }
  }

  return connectionLines.join(' ');
}

/**
 * Build alt text map from structure labels.
 *
 * Traverses the SVGStructure tree and creates a map of element IDs to their
 * human-readable labels. This map can be passed to HTMLStructureTree for
 * accessible descriptions.
 *
 * PIPH-112: When featureResult is provided, also generates feature-level
 * descriptions with connection information (e.g., "Connected at carbon-3 to 4-carbon chain").
 *
 * PIPH-113: When `workspace` is also provided, molecule descriptions are computed
 * per-molecule using the workspace's connected components as the authoritative
 * atom set. This ensures hydrogens nested under non-H atoms in the structure
 * tree (e.g. H₂O's protons hanging off the oxygen's `hydrogenNodes`) are
 * included in the molecular-formula fallback inside `describeSingleMolecule`.
 *
 * @param structure - The SVGStructure to extract labels from
 * @param featureResult - Feature extraction result (required for connection and per-molecule summaries)
 * @param workspace - Required alongside `featureResult` for correct per-molecule summaries
 * @returns A map of element IDs to alt text entries (name + optional description)
 */
export function buildAltTextMapFromStructure(
  structure: SVGStructure,
  featureResult?: WorkspaceFeatureResult,
  workspace?: MolecularWorkspace
): TreeLabelMap {
  const map: TreeLabelMap = new Map();

  // primaries, connectionMap, and feature matching are computed inside the
  // molecule loop to ensure labels are consistent with per-molecule
  // serialization. Tree FeatureNodes and SerializedFeatures are joined by
  // `feature.stableId` (= `SerializedFeature.id`, both produced by
  // `stableFeatureId(feature)`).

  // PIPH-113: Use connected components as the authoritative atom set for each
  // molecule. The structure tree nests hydrogens under their bonded non-H atom
  // (via `AtomLeaf.hydrogenNodes`), so a tree-walk that only visits
  // `feature.atoms` + `unclaimedAtoms` misses those hydrogens — causing the
  // molecular-formula fallback in `describeSingleMolecule` to produce "O" for
  // H₂O and H₃O⁺. `findConnectedComponents(workspace)[i]` returns every atom
  // (including hydrogens) for the i-th molecule, matching the ordering
  // `buildStructure` uses to allocate `structure.tree.molecules`.
  const components = workspace ? findConnectedComponents(workspace) : null;
  const totalMolecules = structure.tree.molecules.length;

  // No-op resolver for the no-feature-result fallback path (the recursive
  // helper short-circuits before consulting the resolver when `serialized` is
  // null — this just satisfies the function signature).
  const fallbackResolve: AtomLabelResolver = () => '';

  for (let moleculeIndex = 0; moleculeIndex < totalMolecules; moleculeIndex++) {
    const molecule = structure.tree.molecules[moleculeIndex];

    // Hoisted so the unclaimed-atoms loop (after the if/else) can still reach
    // the per-molecule serialization for atom descriptions.
    let unclaimedAtomsMoleculeSerialized: SerializationResult | undefined;
    let unclaimedAtomsSerializedAtomsById: Map<string, SerializedAtom> | undefined;
    let unclaimedAtomsResolve: AtomLabelResolver | undefined;

    if (featureResult && workspace && components) {
      const componentAtomIds = components[moleculeIndex].atomIds;
      const moleculeAtomIds = new Set(componentAtomIds);
      const moleculeAtomSymbols: string[] = [];
      for (const atomId of componentAtomIds) {
        const symbol = workspace.getAtom(atomId)?.symbol;
        if (symbol) {
          moleculeAtomSymbols.push(symbol);
        }
      }

      // Filter features to only those in THIS molecule
      const moleculeFeatures = featureResult.features.filter(wf =>
        wf.atomIds.some(atomId => moleculeAtomIds.has(atomId))
      );

      // Serialize just this molecule's features
      const moleculeSerialized = serializeFeatures(
        moleculeFeatures.map(wf => wf.feature),
        featureResult.graph
      );
      unclaimedAtomsMoleculeSerialized = moleculeSerialized;

      // Per-molecule atom label resolver — pre-indexes feature lookups so all
      // downstream description code does O(1) per-feature label resolution.
      const resolve = makeAtomLabelResolver(moleculeSerialized);
      unclaimedAtomsResolve = resolve;

      const moleculePrimaries = moleculeSerialized.features.filter(f => isPrimaryType(f.type));
      const moleculeConnectionMap = computePrimaryConnections(moleculeSerialized, moleculePrimaries);

      // PIPH-150: Prepare data for buildFeatureBlock (same as buildStructuredAltText uses)
      const moleculeFunctionalGroupsByParent = groupFunctionalGroupsByParent(moleculeSerialized);
      const atomFeatures = moleculeSerialized.features.filter(f => f.type === 'atom');
      const moleculeUnclaimedAtoms = classifyUnclaimedAtoms(atomFeatures, moleculePrimaries, moleculeSerialized);
      const moleculeAttachedAtomsByParent = groupUnclaimedAtomsByParent(moleculeUnclaimedAtoms);
      const moleculeBridgingAtoms = moleculeUnclaimedAtoms.filter(a => a.bridging !== null);

      // Tree FeatureNodes and SerializedFeatures share `stableFeatureId(feature)`
      // — the structure-tree carries it as `feature.stableId`, the serializer
      // assigns it as `SerializedFeature.id`. They're two views of the same
      // Feature instance, joined here by that content-hash id.
      const serializedById = new Map<string, SerializedFeature>(
        moleculeSerialized.features.map(sf => [sf.id, sf])
      );
      // SerializedAtom.id is the workspace atom id (when known). Build a
      // lookup so the atom-side join is also a direct Map.get rather than two
      // O(n) scans per atom description.
      const serializedAtomsById = new Map<string, SerializedAtom>();
      for (const sa of moleculeSerialized.atoms) {
        if (sa.id !== undefined) serializedAtomsById.set(sa.id, sa);
      }
      unclaimedAtomsSerializedAtomsById = serializedAtomsById;

      // PIPH-109: pass complete atom symbols so `describeSingleMolecule` can
      // fall back to a molecular formula for feature-less molecules (water,
      // hydronium, etc.)
      const netCharge = componentAtomIds.reduce(
        (sum, id) => sum + (workspace.getAtom(id)?.charge ?? 0), 0
      );
      // Pass the per-molecule resolver so molecule summaries and feature
      // descriptions all use per-feature labels with default-feature fallback.
      const moleculeSummary = describeSingleMolecule(
        moleculeSerialized,
        moleculeIndex,
        totalMolecules,
        moleculeAtomSymbols,
        netCharge,
        resolve,
      );
      map.set(molecule.id, { name: molecule.label, description: moleculeSummary });

      // Process features using per-molecule serialization
      for (const feature of molecule.features) {
        // PIPH-112: Generate feature description with connections
        // PIPH-132: Also generate descriptions for functional groups
        const serializedFeature = serializedById.get(feature.stableId);

        if (serializedFeature) {
          if (serializedFeature.type === 'functional_group') {
            // PIPH-132: Generate functional group description
            const fgDesc = buildFunctionalGroupDescription(serializedFeature, moleculeSerialized, resolve);
            if (fgDesc) {
              map.set(feature.id, { name: feature.label, description: fgDesc });
            } else {
              map.set(feature.id, { name: feature.label });
            }
          } else {
            // PIPH-150: Primary feature - use buildFeatureBlock for consistent descriptions
            // This ensures HTML tree descriptions match LLM text descriptions
            const functionalGroups = moleculeFunctionalGroupsByParent.get(serializedFeature.id) || [];
            const attachedAtoms = moleculeAttachedAtomsByParent.get(serializedFeature.id) || [];
            // Get bridging atoms that touch this primary
            const relevantBridgingAtoms = moleculeBridgingAtoms.filter(
              a => a.bridging && a.bridging.some(p => p.id === serializedFeature.id)
            );

            const block = buildFeatureBlock(
              serializedFeature,
              functionalGroups,
              attachedAtoms,
              relevantBridgingAtoms,
              moleculeSerialized,
              moleculePrimaries,
              moleculeConnectionMap,
              resolve,
            );

            // Convert block.lines to description string
            // Filter out placeholder lines and convert list format to sentences
            // PIPH-325: terminate each substituent/connection phrase with a
            // period so the aggregated description reads as sentences
            // ("bromide at carbon-2. methyl group at carbon-3. ...") rather
            // than a run-on. block.lines entries carry no trailing period; the
            // defensive strip guards against any that ever do.
            const lineDesc = block.lines
              .filter(line => line !== '- (no additional functional groups)')
              .map(line => line.replace(/^- /, '').replace(/\.\s*$/, ''))
              .map(line => `${line}.`)
              .join(' ');

            // Pattern D groups (heteroatoms integrated in a ring/chain) are
            // folded into block.displayName as "{feature name} with oxygen at
            // position N and ...". Surface that phrasing in the screen-reader
            // description so the visible tree label can stay terse.
            const withPrefix = `${serializedFeature.name} with `;
            const patternDClause = block.displayName.startsWith(withPrefix)
              ? `With ${block.displayName.slice(withPrefix.length)}.`
              : '';

            const description = [patternDClause, lineDesc].filter(Boolean).join(' ');

            if (description) {
              map.set(feature.id, { name: feature.label, description });
            } else {
              map.set(feature.id, { name: feature.label });
            }
          }
        } else {
          map.set(feature.id, { name: feature.label });
        }

        for (const atom of feature.atoms) {
          map.set(
            atom.atomId,
            buildAtomEntry(
              atom.atomId,
              atom.label,
              moleculeSerialized,
              serializedAtomsById,
              resolve,
            ),
          );
          for (const lp of atom.lonePairNodes) {
            map.set(lp.lonePairId, { name: lp.label });
          }
        }
        // Bonds (collected from atoms' bondNodes, deduplicated)
        const seenFeatureBondIds = new Set<string>();
        for (const atom of feature.atoms) {
          for (const bond of atom.bondNodes) {
            if (!seenFeatureBondIds.has(bond.bondId)) {
              seenFeatureBondIds.add(bond.bondId);
              map.set(bond.bondId, { name: bond.label });
            }
          }
        }

        // PIPH-309 alignment: overlay bond labels and lone-pair labels so they
        // agree with the atom labels `buildAtomEntry` already wrote above.
        if (serializedFeature) {
          overlayFeatureChildLabels(feature, map, serializedAtomsById, resolve);
        }

        // PIPH-112: Also process child features (nested features)
        for (const childFeature of feature.children) {
          processFeatureRecursive(
            childFeature, map, moleculeSerialized, moleculePrimaries, moleculeConnectionMap, resolve, serializedById,
            moleculeFunctionalGroupsByParent, moleculeAttachedAtomsByParent, moleculeBridgingAtoms,
            serializedAtomsById,
          );
        }
      }
    } else {
      // No feature result - just set basic labels
      map.set(molecule.id, { name: molecule.label });

      for (const feature of molecule.features) {
        map.set(feature.id, { name: feature.label });

        for (const atom of feature.atoms) {
          map.set(atom.atomId, { name: atom.label });
          for (const lp of atom.lonePairNodes) {
            map.set(lp.lonePairId, { name: lp.label });
          }
        }
        // Bonds (collected from atoms' bondNodes, deduplicated)
        const seenFeatureBondIds = new Set<string>();
        for (const atom of feature.atoms) {
          for (const bond of atom.bondNodes) {
            if (!seenFeatureBondIds.has(bond.bondId)) {
              seenFeatureBondIds.add(bond.bondId);
              map.set(bond.bondId, { name: bond.label });
            }
          }
        }

        // Process child features without descriptions (no feature result available).
        // Pass an identity resolver — child features will only emit their bare
        // labels (the recursive helper short-circuits when serialized is null).
        for (const childFeature of feature.children) {
          processFeatureRecursive(childFeature, map, null, [], null, fallbackResolve, undefined, undefined, undefined, undefined);
        }
      }
    }

    // Unclaimed atoms and bonds
    for (const atom of molecule.unclaimedAtoms) {
      // Generate a per-atom description when serialization context is
      // available; otherwise fall back to name-only.
      const entry = (featureResult && unclaimedAtomsMoleculeSerialized && unclaimedAtomsSerializedAtomsById && unclaimedAtomsResolve)
        ? buildAtomEntry(atom.atomId, atom.label, unclaimedAtomsMoleculeSerialized, unclaimedAtomsSerializedAtomsById, unclaimedAtomsResolve)
        : { name: atom.label };
      map.set(atom.atomId, entry);
      for (const lp of atom.lonePairNodes) {
        map.set(lp.lonePairId, { name: lp.label });
      }
    }
    // Bonds in unclaimed atoms (collected from bondNodes, deduplicated)
    const seenUnclaimedBondIds = new Set<string>();
    for (const atom of molecule.unclaimedAtoms) {
      for (const bond of atom.bondNodes) {
        if (!seenUnclaimedBondIds.has(bond.bondId)) {
          seenUnclaimedBondIds.add(bond.bondId);
          map.set(bond.bondId, { name: bond.label });
        }
      }
    }
  }

  // PIPH-90: Add arrows to alt text map
  if (structure.tree.arrows) {
    for (const arrow of structure.tree.arrows) {
      map.set(arrow.id, {
        name: arrow.label,
        description: arrow.description,
      });
    }
  }

  return map;
}

/**
 * Helper to recursively process nested features and add connection descriptions.
 * Per-feature naming: `resolve` is the per-molecule AtomLabelResolver shared
 * with the parent caller — passing the feature's id as context produces
 * scope-specific labels.
 *
 * Joins tree features to serialized features by `feature.stableId`
 * (= `SerializedFeature.id`).
 */
function processFeatureRecursive(
  feature: FeatureNode,
  map: TreeLabelMap,
  serialized: SerializationResult | null,
  primaries: SerializedFeature[],
  connectionMap: Map<string, PrimaryConnection[]> | null,
  resolve: AtomLabelResolver,
  serializedById?: Map<string, SerializedFeature>,
  functionalGroupsByParent?: Map<string, SerializedFeature[]>,
  attachedAtomsByParent?: Map<string, UnclaimedAtom[]>,
  bridgingAtoms?: UnclaimedAtom[],
  serializedAtomsById?: Map<string, SerializedAtom>,
): void {
  // Track the serialized counterpart of this feature so we can pass its id as
  // featureContextId when describing the feature's atoms.
  let matchedSerializedFeature: SerializedFeature | undefined;
  if (serialized && connectionMap) {
    const serializedFeature = serializedById?.get(feature.stableId);
    matchedSerializedFeature = serializedFeature;

    if (serializedFeature) {
      if (serializedFeature.type === 'functional_group') {
        const fgDesc = buildFunctionalGroupDescription(serializedFeature, serialized, resolve);
        if (fgDesc) {
          map.set(feature.id, { name: feature.label, description: fgDesc });
        } else {
          map.set(feature.id, { name: feature.label });
        }
      } else {
        if (functionalGroupsByParent && attachedAtomsByParent && bridgingAtoms) {
          const functionalGroups = functionalGroupsByParent.get(serializedFeature.id) || [];
          const attachedAtoms = attachedAtomsByParent.get(serializedFeature.id) || [];
          const relevantBridgingAtoms = bridgingAtoms.filter(
            a => a.bridging && a.bridging.some(p => p.id === serializedFeature.id)
          );

          const block = buildFeatureBlock(
            serializedFeature,
            functionalGroups,
            attachedAtoms,
            relevantBridgingAtoms,
            serialized,
            primaries,
            connectionMap,
            resolve,
          );

          const description = block.lines
            .filter(line => line !== '- (no additional functional groups)')
            .map(line => line.replace(/^- /, ''))
            .join(' ');

          if (description) {
            map.set(feature.id, { name: feature.label, description });
          } else {
            map.set(feature.id, { name: feature.label });
          }
        } else {
          // Fallback when helper data isn't supplied
          const connectionDesc = buildFeatureConnectionDescription(
            serializedFeature.id,
            serialized,
            primaries,
            connectionMap,
            resolve,
          );
          if (connectionDesc) {
            map.set(feature.id, { name: feature.label, description: connectionDesc });
          } else {
            map.set(feature.id, { name: feature.label });
          }
        }
      }
    } else {
      map.set(feature.id, { name: feature.label });
    }
  } else {
    map.set(feature.id, { name: feature.label });
  }

  for (const atom of feature.atoms) {
    if (serialized && serializedAtomsById) {
      map.set(
        atom.atomId,
        buildAtomEntry(
          atom.atomId,
          atom.label,
          serialized,
          serializedAtomsById,
          resolve,
        ),
      );
    } else {
      map.set(atom.atomId, { name: atom.label });
    }
    for (const lp of atom.lonePairNodes) {
      map.set(lp.lonePairId, { name: lp.label });
    }
  }

  const seenBondIds = new Set<string>();
  for (const atom of feature.atoms) {
    for (const bond of atom.bondNodes) {
      if (!seenBondIds.has(bond.bondId)) {
        seenBondIds.add(bond.bondId);
        map.set(bond.bondId, { name: bond.label });
      }
    }
  }

  // PIPH-309 alignment: overlay bond + lone-pair labels so they follow the
  // same default-feature numbering as the atom labels above.
  if (serialized && serializedAtomsById && matchedSerializedFeature) {
    overlayFeatureChildLabels(feature, map, serializedAtomsById, resolve);
  }

  for (const childFeature of feature.children) {
    processFeatureRecursive(
      childFeature, map, serialized, primaries, connectionMap, resolve, serializedById,
      functionalGroupsByParent, attachedAtomsByParent, bridgingAtoms,
      serializedAtomsById,
    );
  }
}
